import { useState, useEffect } from 'react';
import { HiChevronLeft, HiChevronRight, HiCheckCircle, HiSparkles } from 'react-icons/hi';

// Découpe le contenu en sections lisibles (sur les doubles sauts de ligne)
const decouperEnSections = (texte) => {
  if (!texte) return [];
  const propre = texte.split('\\n').join('\n');
  const blocs = propre.split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
  // Regrouper par 2 blocs pour des pages ni trop courtes ni trop longues
  const pages = [];
  for (let i = 0; i < blocs.length; i += 2) {
    pages.push(blocs.slice(i, i + 2).join('\n\n'));
  }
  return pages;
};

// Rendu simple du pseudo-markdown (##, **, listes -)
const RenduBloc = ({ texte }) => (
  <div className="space-y-3">
    {texte.split('\n').map((ligne, i) => {
      if (ligne.startsWith('## ')) {
        return <h2 key={i} className="text-xl font-bold text-gray-800">{ligne.replace('## ', '')}</h2>;
      }
      if (ligne.startsWith('- ')) {
        return (
          <div key={i} className="flex items-start gap-2 ml-2">
            <span className="text-blue-500 mt-1">•</span>
            <span className="text-gray-700">{ligne.replace('- ', '').split('**').join('')}</span>
          </div>
        );
      }
      if (!ligne.trim()) return null;
      // Gras **...** rendu en <strong>
      const morceaux = ligne.split('**');
      return (
        <p key={i} className="text-gray-700 leading-relaxed">
          {morceaux.map((m, j) => j % 2 === 1 ? <strong key={j} className="text-gray-900">{m}</strong> : m)}
        </p>
      );
    })}
  </div>
);

const LecteurTexte = ({ contenu, onTerminerLecture, lectureTerminee }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const pages = decouperEnSections(contenu);

  useEffect(() => { setPageIndex(0); }, [contenu]);

  if (pages.length === 0) return null;

  const estDerniere = pageIndex === pages.length - 1;
  const progression = Math.round(((pageIndex + 1) / pages.length) * 100);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Barre de progression de lecture */}
      <div className="h-2 bg-gray-100">
        <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500" style={{ width: `${progression}%` }}></div>
      </div>

      <div className="p-8 min-h-[320px]">
        <div className="flex items-center justify-between mb-6">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-500 bg-blue-50 px-3 py-1 rounded-full">
            Étape {pageIndex + 1} / {pages.length}
          </span>
          <span className="text-xs text-gray-400">{progression}% lu</span>
        </div>

        <RenduBloc texte={pages[pageIndex]} />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-8 py-5 bg-gray-50 border-t border-gray-100">
        <button
          onClick={() => setPageIndex(p => Math.max(0, p - 1))}
          disabled={pageIndex === 0}
          className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <HiChevronLeft /> Précédent
        </button>

        {/* Points d'étape */}
        <div className="flex gap-1.5">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => setPageIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${i === pageIndex ? 'bg-blue-500 scale-125' : i < pageIndex ? 'bg-blue-300' : 'bg-gray-300'}`}
              aria-label={`Étape ${i + 1}`}
            />
          ))}
        </div>

        {!estDerniere ? (
          <button
            onClick={() => setPageIndex(p => p + 1)}
            className="flex items-center gap-1 px-5 py-2 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-all hover:scale-[1.02]"
          >
            Suivant <HiChevronRight />
          </button>
        ) : lectureTerminee ? (
          <span className="flex items-center gap-1 px-4 py-2 text-sm font-bold text-green-600">
            <HiCheckCircle className="text-lg" /> Lecture terminée
          </span>
        ) : (
          <button
            onClick={onTerminerLecture}
            className="flex items-center gap-1 px-5 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 shadow-md shadow-green-500/20 transition-all hover:scale-[1.02]"
          >
            <HiSparkles /> J'ai tout lu !
          </button>
        )}
      </div>
    </div>
  );
};

export default LecteurTexte;
